/**
 * Chart operations the taskpane drives. These exist so `ui/` never opens an `Excel.run` of its own:
 * Office batches cannot nest or run concurrently, so every one belongs on this side of the boundary
 * where the batching is visible in one place.
 */

import { splitChartTitle } from "../helpers/common";

/** The live chart type and title, so a card follows option changes and edits made in Excel. */
export const getChartDisplay = async (
  worksheetName: string,
  chartName: string
): Promise<{ chartType: Excel.ChartType; title: string; subTitle: string }> =>
  Excel.run(async (context) => {
    const chart = context.workbook.worksheets.getItem(worksheetName).charts.getItem(chartName);
    chart.load("chartType,title/text");
    await context.sync();
    return { chartType: chart.chartType as Excel.ChartType, ...splitChartTitle(chart.title.text) };
  });

/** Brings the chart into view, activating its sheet first. */
export const activateChart = async (worksheetName: string, chartName: string): Promise<void> =>
  Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(worksheetName);
    sheet.activate();
    sheet.charts.getItem(chartName).activate();
    await context.sync();
  });

/** Removes the chart, its source table, and the hidden sheet that holds its data. */
export const deleteChart = async (worksheetName: string, chartName: string): Promise<void> =>
  Excel.run(async (context) => {
    context.workbook.worksheets.getItem(worksheetName).charts.getItemOrNullObject(chartName).delete();
    context.workbook.tables.getItemOrNullObject(chartName).delete();
    // Each chart owns its hidden data sheet, so this can't take another chart's data with it —
    // several charts may share a visible sheet, but never this one.
    context.workbook.worksheets.getItemOrNullObject(`__${chartName}`).delete();
    await context.sync();
  });
